import { useEffect, useMemo, useRef, useState } from 'react';

type ModalStateAction =
  | {
      type: 'pending';
    }
  | {
      type: 'success';
    }
  | {
      type: 'error';
      title: string;
      description: string;
    };

export type ModalStateProps = {
  action?: ModalStateAction;
  forceClose?: boolean;
  isClosingDisabled?: boolean;
};

export const useModalState = (): {
  modalProps: ModalStateProps;
  trigger: {
    action: () => void;
    error: (props: { title: string; description: string }) => void;
    close: () => void;
  };
} => {
  const [modalProps, setModalProps] = useState<ModalStateProps>({});

  const isMountedRef = useRef(true);

  const trigger = useMemo(
    () => ({
      action: () => {
        setModalProps({
          isClosingDisabled: true,
          action: {
            type: 'pending',
          },
        });
      },
      error: (error: { title: string; description: string }) => {
        setModalProps({
          action: {
            type: 'error',
            ...error,
          },
        });
      },
      close: () => {
        setModalProps((prev) => ({
          ...prev,
          forceClose: true,
          isClosingDisabled: false,
        }));
      },
    }),
    []
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    trigger,
    modalProps,
  };
};
